import { useEffect, useRef, type ReactNode } from 'react';
import './MobileSheet.css';

interface Props {
  title: string;
  /** One line under the title saying what this sheet is for. */
  hint?: string;
  onClose: () => void;
  children: ReactNode;
}

/**
 * A panel that rises from the bottom edge over a dimmed backdrop.
 *
 * Bottom-anchored rather than centred like the desktop `Dialog`: a phone is
 * held low and reached with a thumb, so a centred window puts its controls
 * exactly where the hand is not. Everything tappable therefore lives in the
 * lower half, and the grab handle at the top is the part you never need to hit.
 */
export function MobileSheet({ title, hint, onClose, children }: Props) {
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    // The sheet owns the screen while it is open; the list underneath must not
    // scroll away behind it.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return (
    <div
      className="sheet__scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="sheet" ref={panel} role="dialog" aria-modal="true" aria-label={title}>
        <div className="sheet__grip" aria-hidden="true" />
        <header className="sheet__head">
          <h2 className="sheet__title">{title}</h2>
          {hint && <p className="sheet__hint">{hint}</p>}
        </header>
        <div className="sheet__body">{children}</div>
        <button type="button" className="sheet__close" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

/** One tappable line in a sheet. `tone="danger"` for the destructive ones. */
export function SheetRow({
  label,
  detail,
  tone,
  onClick,
}: {
  label: string;
  detail?: string;
  tone?: 'danger';
  onClick: () => void;
}) {
  return (
    <button type="button" className={`sheetRow${tone ? ` sheetRow--${tone}` : ''}`} onClick={onClick}>
      <span className="sheetRow__label">{label}</span>
      {detail && <span className="sheetRow__detail">{detail}</span>}
    </button>
  );
}

/** A labelled group of controls inside a sheet. */
export function SheetField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="sheetField">
      <span className="sheetField__label">{label}</span>
      <div className="sheetField__control">{children}</div>
    </div>
  );
}
